import {noop} from '@benchling/js-utils';
import React, {useEffect, useMemo, useState} from 'react';

import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {getDefaultChartConfig} from 'analysis-tool/ui/charting/ChartingUtils';
import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigForm from 'pipelines/generalized-step-config-editor/editors/StepConfigForm';
import {STEP_CONFIG_SETTINGS_V2} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettings';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {ConfigShapePath} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {
  stepTypeIsConfigEditorSupported,
  isV2ConfigEditorStepType,
} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepConfig,
  PipelineViewGraphQL,
  assertPipelineStepIsStepType,
} from 'pipelines/PipelineTypes';
import {getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {JSONValue} from 'util/Types';

import useMaybeDraftStep from '../useMaybeDraftStep';
import ChartTypeSwitcher, {ChartTypeSwitcherValue} from './ChartTypeSwitcher';
import {ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE, preserveSharedFields} from './chartTypeSwitchUtils';

type ChartViewConfig = PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>;

interface EditViewChartProps {
  analysis: PipelineGraphQL;
  clientStateId: string;
  step: PipelineStep<PipelineStepType.CHART_VIEW, 0>;
  view: PipelineViewGraphQL;
  isDisabled: boolean;
  dataFrameColumns: DataFrameColumns;
  onSwitchToTable: () => void;
}

export default function EditViewChart({
  analysis,
  clientStateId,
  step,
  view,
  isDisabled,
  dataFrameColumns,
  onSwitchToTable,
}: EditViewChartProps): JSX.Element {
  // We only support updating Charts that are outputs of CHART_VIEW steps.
  const {stepType, stepVersion} = step;

  // Store a whole step as state, rather than just `draftConfig`, so the draft config does not go out-of-sync
  // with `stepType` and `inputStepDatas` during async updates.
  // TODO(BNCH-121407): Clear the draft step when the step is deleted, and update it to work for multiple tabs.
  const {draftStep, setDraftStep} = useMaybeDraftStep(step.id, {...step, config: {...step.config}});
  assert(draftStep != null, 'Expected draft step to be non-null');
  assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, draftStep);

  const [localConfig, setLocalConfig] = useState<Partial<ChartViewConfig>>(draftStep.config ?? {});
  const debouncedConfig = useDebounce(localConfig, 300);
  const updatedDraftStep = useMemo(
    () => ({...draftStep, config: debouncedConfig}),
    [draftStep, debouncedConfig]
  );

  // Update draft step with debounced config
  useEffect(() => {
    setDraftStep(updatedDraftStep);
  }, [updatedDraftStep, setDraftStep]);

  // Sync local config when step changes externally
  useEffect(() => {
    setLocalConfig(draftStep.config ?? {});
  }, [step.id, draftStep.config]);

  const currentChartType = (localConfig.chartConfig?.type ??
    draftStep.config?.chartConfig?.type ??
    step.config?.chartConfig?.type) as AnalysisVisualizationType;

  const handleChartTypeChange = (newType: ChartTypeSwitcherValue): void => {
    if (newType === AnalysisVisualizationType.TABLE) {
      onSwitchToTable();
      return;
    }

    const chartOptionType =
      ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE[
        newType as Exclude<
          AnalysisVisualizationType,
          AnalysisVisualizationType.TABLE | AnalysisVisualizationType.CONTOUR_PLOT
        >
      ];
    const newDefaultConfig = getDefaultChartConfig(chartOptionType, dataFrameColumns);
    const newChartConfig = localConfig.chartConfig
      ? preserveSharedFields(localConfig.chartConfig, newDefaultConfig.chartConfig)
      : newDefaultConfig.chartConfig;

    const newConfig = {
      ...localConfig,
      chartConfig: newChartConfig,
      chartName: localConfig.chartName ?? newDefaultConfig.chartName,
    };
    setLocalConfig(newConfig);
  };

  if (!stepTypeIsConfigEditorSupported(stepType, stepVersion)) {
    assertAndContinue(false, `StepConfigEditorPane used for ${stepType} v${stepVersion}`);
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No configuration is available." />;
  }

  assert(isV2ConfigEditorStepType(stepType), 'Expected step type to be supported in StepConfigForm', {
    stepType,
    stepVersion,
  });
  const {uiSchema} = STEP_CONFIG_SETTINGS_V2[stepType][stepVersion];

  return (
    <>
      <ChartTypeSwitcher
        currentType={currentChartType}
        onChange={handleChartTypeChange}
        isDisabled={isDisabled}
      />
      <StepConfigForm
        config={localConfig}
        configShape={getStepTypeConfigShape(stepType, stepVersion)}
        clientStateId={clientStateId}
        disabled={isDisabled}
        onChangeConfig={
          isDisabled
            ? noop
            : (config: Record<string, JSONValue | null>, path: ConfigShapePath, newValue: JSONValue) => {
                setLocalConfig(setValueInConfig(config ?? {}, path, newValue) as Partial<ChartViewConfig>);
              }
        }
        uiSchema={uiSchema}
      />
    </>
  );
}
